import { TriangleAlert } from 'lucide-react';
import { useState } from 'react';
import { priceTableApi } from '@/api/commercial/price-tables';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';

interface CancelPriceTableDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  priceTableId?: string;
  onSuccess?: () => void;
}

export function CancelPriceTableDialog({
  open,
  onOpenChange,
  priceTableId,
  onSuccess,
}: CancelPriceTableDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const handleCancel = async () => {
    if (!priceTableId) {
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: 'ID da tabela não encontrado.',
      });
      return;
    }

    setIsLoading(true);
    try {
      const result = await priceTableApi.cancel(priceTableId);

      if (result.success) {
        toast({
          variant: 'success',
          title: 'Sucesso',
          description: 'Tabela cancelada com sucesso!',
        });
        onOpenChange(false);
        onSuccess?.();
        window.location.reload();
      } else {
        toast({
          variant: 'destructive',
          title: 'Erro',
          description: result.error?.global || 'Erro ao cancelar tabela. Tente novamente.',
        });
      }
    } catch (_error) {
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: 'Erro ao cancelar tabela. Tente novamente.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100%-2rem)] max-w-md gap-4 rounded-lg p-5 [&>button]:hidden">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-red-100">
            <TriangleAlert className="h-5 w-5 text-red-500" aria-hidden="true" />
          </div>
          <DialogHeader className="space-y-2 pb-0 text-left">
            <DialogTitle className="text-base leading-5">Cancelar tabela!</DialogTitle>
            <DialogDescription className="text-xs leading-[18px] text-gray-500">
              Ao confirmar esta ação, não será mais possível utilizar a tabela em questão.
              Posteriormente poderá ser clonada apenas.
            </DialogDescription>
          </DialogHeader>
        </div>
        <DialogFooter className="flex-row justify-end gap-2 pt-0 sm:space-x-0">
          <Button
            size="sm"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancelar
          </Button>
          <Button size="sm" variant="destructive" onClick={handleCancel} disabled={isLoading}>
            {isLoading ? 'Cancelando...' : 'Confirmar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
